/**
 * 🗓️ ProgramPlanner — Self Workout + Food planner (Feb 2026, owner-locked).
 *
 * Owner directives:
 *   • "self workout log/AI to have programs that allow easy access to
 *      food/exercises, I am doing today/week/month or cycle"
 *   • Creation: manual + AI-generated + edit AI template
 *   • Cycles:   fixed 4w · custom weeks · open-ended rolling
 *   • Two tabs: Food | Workout
 *   • Phases per week (bulk / cut / deload / …)
 *   • Placement: inside /interactive-gym as a tab
 *
 * MVP:
 *   Today card · Week strip · Cycle grid.
 *   Phase-per-week picker inline.
 *   AI Generate button (calls /program/ai-generate).
 *   Manual day edit drawer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles, Loader2, Plus, X, Check, ChevronLeft, ChevronRight,
  ChefHat, Dumbbell, Trash2, Calendar as CalendarIcon,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// PT date helpers (match FoodLog / server)
function todayPTKey() {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Los_Angeles",
    year: "numeric", month: "2-digit", day: "2-digit",
  }).formatToParts(new Date());
  return `${parts.find(p=>p.type==='year').value}-${parts.find(p=>p.type==='month').value}-${parts.find(p=>p.type==='day').value}`;
}
function addDays(iso, n) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
function fmtShort(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", timeZone: "UTC" });
}
function fmtDay(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
}
function fmtNum(iso) {
  const d = new Date(`${iso}T12:00:00Z`);
  return d.toLocaleDateString("en-US", { day: "numeric", timeZone: "UTC" });
}

function weekIndex(startISO, dayISO) {
  const a = new Date(`${startISO}T12:00:00Z`);
  const b = new Date(`${dayISO}T12:00:00Z`);
  const diff = Math.floor((b - a) / (1000 * 60 * 60 * 24));
  return Math.max(0, Math.floor(diff / 7));
}

export function ProgramPlanner() {
  const [program, setProgram] = useState(null);
  const [phaseCatalog, setPhaseCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("today"); // today | week | cycle
  const [selectedDay, setSelectedDay] = useState(todayPTKey());
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorTab, setEditorTab] = useState("workout"); // workout | food
  const [busy, setBusy] = useState(false);

  // Wizard state (shown when no program exists)
  const [wizardGoal, setWizardGoal] = useState("");
  const [wizardMode, setWizardMode] = useState("fixed_4w");
  const [wizardWeeks, setWizardWeeks] = useState(4);

  // Day editor state
  const [dayWorkout, setDayWorkout] = useState([]);
  const [dayMeals, setDayMeals] = useState([]);
  const [newWName, setNewWName] = useState("");
  const [newWSets, setNewWSets] = useState("");
  const [newWReps, setNewWReps] = useState("");
  const [newMMeal, setNewMMeal] = useState("Breakfast");
  const [newMName, setNewMName] = useState("");
  const editorScrollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [a, p] = await Promise.all([
        axios.get(`${API}/program/active`, { withCredentials: true }),
        axios.get(`${API}/program/phases`, { withCredentials: true }),
      ]);
      setProgram(a.data?.program || null);
      setPhaseCatalog(p.data?.phases || []);
    } catch {
      setProgram(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const phaseById = useMemo(() => {
    const m = {};
    for (const p of phaseCatalog) m[p.id] = p;
    return m;
  }, [phaseCatalog]);

  const startDate = program?.start_date || todayPTKey();
  const weeks = program?.weeks || (program?.mode === "rolling" ? 4 : 4);
  const days = program?.days || {};

  // Load day into editor when the modal opens
  useEffect(() => {
    if (!editorOpen) return;
    const d = days[selectedDay] || { workout: [], meals: [] };
    setDayWorkout(d.workout || []);
    setDayMeals(d.meals || []);
    setNewWName(""); setNewWSets(""); setNewWReps("");
    setNewMMeal("Breakfast"); setNewMName("");
    setTimeout(() => editorScrollRef.current?.scrollTo?.({ top: 0 }), 50);
  }, [editorOpen, selectedDay, days]);

  const openEditor = (day) => {
    setSelectedDay(day);
    setEditorOpen(true);
  };

  // ─── Wizard actions ───
  const onCreateEmpty = async () => {
    if (!wizardGoal.trim()) {
      toast.error("A short goal helps ('build strength', 'move daily', …)");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/program/create`, {
        goal: wizardGoal,
        mode: wizardMode,
        weeks: wizardMode === "custom" ? wizardWeeks : undefined,
        title: `${wizardGoal.slice(0, 40)} · ${wizardMode === "rolling" ? "rolling" : `${wizardMode === "fixed_4w" ? 4 : wizardWeeks}w`}`,
        activate: true,
      }, { withCredentials: true });
      setProgram(r.data?.program || null);
      toast.success("Program created — start filling days below.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create program.");
    } finally { setBusy(false); }
  };

  const onAiGenerate = async () => {
    if (!wizardGoal.trim()) {
      toast.error("Add a short goal first so the AI knows what to design.");
      return;
    }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/program/ai-generate`, {
        goal: wizardGoal,
        mode: wizardMode,
        weeks: wizardMode === "custom" ? wizardWeeks : undefined,
      }, { withCredentials: true });
      setProgram(r.data?.program || null);
      toast.success("AI drafted your program — edit any day to tweak.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "AI generate failed.");
    } finally { setBusy(false); }
  };

  const onSetPhase = async (weekIdx, phaseId) => {
    if (!program) return;
    const next = [...(program.phases || [])];
    while (next.length <= weekIdx) next.push("maintain");
    next[weekIdx] = phaseId;
    try {
      const r = await axios.patch(`${API}/program/${program.program_id}`, { phases: next }, { withCredentials: true });
      setProgram(r.data?.program || program);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Phase update failed.");
    }
  };

  const onSaveDay = async () => {
    if (!program) return;
    setBusy(true);
    try {
      const r = await axios.put(
        `${API}/program/${program.program_id}/day/${selectedDay}`,
        { workout: dayWorkout, meals: dayMeals },
        { withCredentials: true },
      );
      setProgram(r.data?.program || program);
      setEditorOpen(false);
      toast.success(`Saved ${fmtShort(selectedDay)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  const onClearDay = async () => {
    if (!program) return;
    if (!window.confirm(`Clear ${fmtShort(selectedDay)}?`)) return;
    try {
      const r = await axios.delete(`${API}/program/${program.program_id}/day/${selectedDay}`, { withCredentials: true });
      setProgram(r.data?.program || program);
      setEditorOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed.");
    }
  };

  const onDeleteProgram = async () => {
    if (!program) return;
    if (!window.confirm("Delete this program permanently?")) return;
    try {
      await axios.delete(`${API}/program/${program.program_id}`, { withCredentials: true });
      setProgram(null);
      setWizardGoal("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed.");
    }
  };

  // ─── Empty state / wizard ───
  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-black/40 p-6 text-center text-white/60 text-sm">
        <Loader2 className="animate-spin inline mr-2" size={14} /> loading your program…
      </div>
    );
  }

  if (!program) {
    return (
      <div className="rounded-2xl border border-sky-500/30 bg-gradient-to-br from-sky-950/40 via-black to-black p-5 space-y-4" data-testid="program-wizard">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-sky-500/20 border border-sky-400/40">
            <CalendarIcon size={16} className="text-sky-300" />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">Program</div>
            <div className="text-lg font-black text-white leading-tight">Build your training block</div>
            <div className="text-xs text-white/60">Today · This Week · This Month · Cycle — all in one place.</div>
          </div>
        </div>

        <label className="block space-y-1">
          <span className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Your goal</span>
          <input
            type="text"
            value={wizardGoal}
            onChange={(e) => setWizardGoal(e.target.value)}
            placeholder="e.g. build strength · cut carefully · move every day"
            className="w-full bg-black/60 border border-white/15 rounded px-3 py-2 text-sm text-white placeholder-white/40 outline-none focus:border-sky-400/60"
            data-testid="program-wizard-goal"
          />
        </label>

        <div className="grid grid-cols-3 gap-2">
          {[
            { id: "fixed_4w", label: "Fixed 4 wk" },
            { id: "custom",   label: "Custom" },
            { id: "rolling",  label: "Rolling" },
          ].map((m) => (
            <button
              key={m.id}
              type="button"
              onClick={() => setWizardMode(m.id)}
              className={`px-2 py-2 rounded text-xs font-bold uppercase tracking-wide border transition ${
                wizardMode === m.id
                  ? "bg-sky-500 border-sky-300 text-black"
                  : "bg-black/40 border-white/15 text-white/70 hover:border-white/30"
              }`}
              data-testid={`program-wizard-mode-${m.id}`}
            >
              {m.label}
            </button>
          ))}
        </div>

        {wizardMode === "custom" && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-white/70">Weeks:</span>
            {[2, 4, 6, 8, 12].map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => setWizardWeeks(w)}
                className={`w-9 h-9 rounded-full text-xs font-black border transition ${
                  wizardWeeks === w
                    ? "bg-white text-black border-white"
                    : "bg-black/40 text-white/70 border-white/15 hover:border-white/40"
                }`}
                data-testid={`program-wizard-weeks-${w}`}
              >
                {w}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2 pt-1">
          <button
            type="button"
            onClick={onAiGenerate}
            disabled={busy}
            className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-amber-500 text-black text-xs font-black uppercase tracking-widest hover:bg-amber-400 disabled:opacity-60"
            data-testid="program-wizard-ai"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            AI Draft a cycle
          </button>
          <button
            type="button"
            onClick={onCreateEmpty}
            disabled={busy}
            className="flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg bg-black/60 border border-white/25 text-white text-xs font-bold uppercase tracking-widest hover:bg-white/10 disabled:opacity-60"
            data-testid="program-wizard-manual"
          >
            <Plus size={14} /> Start blank
          </button>
        </div>
        <div className="text-[10px] text-white/40 italic">
          AI uses your scanned equipment + pantry to shape workouts and meals.
        </div>
      </div>
    );
  }

  // ─── Program view ───
  const todayKey = todayPTKey();
  const totalDays = weeks * 7;
  const dayList = Array.from({ length: totalDays }, (_, i) => addDays(startDate, i));
  const todayIdx = Math.max(0, Math.min(totalDays - 1, dayList.indexOf(todayKey)));

  // For "week" view — the week containing today
  const weekStartIdx = Math.floor(todayIdx / 7) * 7;
  const weekDays = dayList.slice(weekStartIdx, weekStartIdx + 7);

  const renderDayCell = (day, size = "md") => {
    const d = days[day];
    const wkIdx = weekIndex(startDate, day);
    const phase = phaseById[program.phases?.[wkIdx] || "maintain"];
    const workoutCount = d?.workout?.length || 0;
    const mealCount = d?.meals?.length || 0;
    const isToday = day === todayKey;
    return (
      <button
        key={day}
        type="button"
        onClick={() => openEditor(day)}
        className={`text-left rounded-lg border p-2 transition ${
          isToday
            ? "border-sky-400 bg-sky-950/40 shadow-lg shadow-sky-500/20"
            : "border-white/10 bg-black/40 hover:border-white/30"
        }`}
        style={{ borderLeft: `4px solid ${phase?.color || "#71717a"}` }}
        data-testid={`program-day-${day}`}
      >
        <div className="flex items-center justify-between gap-1">
          <div>
            <div className="text-[9px] uppercase tracking-widest text-white/50">{fmtDay(day)}</div>
            <div className="text-sm font-black text-white leading-none">{fmtNum(day)}</div>
          </div>
          {isToday && <span className="text-[8px] px-1 py-0.5 rounded bg-sky-500 text-black font-bold">TODAY</span>}
        </div>
        {size === "md" && (
          <div className="mt-1.5 space-y-0.5">
            <div className="text-[10px] text-white/70 flex items-center gap-1">
              <Dumbbell size={9} /> {workoutCount || "—"}
            </div>
            <div className="text-[10px] text-white/70 flex items-center gap-1">
              <ChefHat size={9} /> {mealCount || "—"}
            </div>
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="rounded-2xl border border-sky-500/30 bg-gradient-to-br from-sky-950/30 via-black to-black p-5 space-y-4" data-testid="program-planner">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">Program</div>
          <div className="text-lg font-black text-white leading-tight truncate">{program.title}</div>
          <div className="text-xs text-white/60 truncate">
            {program.goal || "No goal set"} · {program.mode === "rolling" ? "rolling" : `${program.weeks}-week cycle`} · started {fmtShort(startDate)}
          </div>
        </div>
        <div className="flex items-center gap-1">
          {["today", "week", "cycle"].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => setView(v)}
              className={`px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-widest border transition ${
                view === v
                  ? "bg-white text-black border-white"
                  : "bg-black/40 text-white/70 border-white/15 hover:border-white/40"
              }`}
              data-testid={`program-view-${v}`}
            >
              {v}
            </button>
          ))}
          <button
            type="button"
            onClick={onDeleteProgram}
            className="p-1.5 rounded bg-black/40 border border-white/15 hover:bg-red-500/30 ml-1"
            title="Delete program"
            data-testid="program-delete"
          >
            <Trash2 size={12} className="text-red-300" />
          </button>
        </div>
      </div>

      {/* Phase strip (per week) */}
      {program.phases?.length > 0 && (
        <div className="flex flex-wrap gap-1.5" data-testid="program-phase-strip">
          {program.phases.map((pid, i) => {
            const ph = phaseById[pid] || { label: pid, color: "#71717a" };
            return (
              <details key={i} className="group">
                <summary
                  className="list-none cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest border border-white/15 bg-black/40 hover:border-white/40"
                  data-testid={`program-phase-${i}`}
                >
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: ph.color }} />
                  Wk {i + 1} · {ph.label}
                </summary>
                <div className="mt-1 flex flex-wrap gap-1 p-1 rounded bg-black/70 border border-white/10 absolute z-10 shadow-xl">
                  {phaseCatalog.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={(e) => { e.currentTarget.closest("details").open = false; onSetPhase(i, p.id); }}
                      className="text-[10px] px-2 py-0.5 rounded bg-white/5 hover:bg-white/20 text-white border border-white/10"
                      data-testid={`program-phase-${i}-set-${p.id}`}
                    >
                      <span className="inline-block w-1.5 h-1.5 rounded-full mr-1" style={{ background: p.color }} />
                      {p.label}
                    </button>
                  ))}
                </div>
              </details>
            );
          })}
        </div>
      )}

      {/* View: today */}
      {view === "today" && (
        <div className="space-y-3" data-testid="program-today">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {/* Workout column */}
            <div className="rounded-lg border border-white/10 bg-black/40 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">
                  <Dumbbell size={11} className="inline" /> Today · workout
                </div>
                <button
                  type="button"
                  onClick={() => { setEditorTab("workout"); openEditor(todayKey); }}
                  className="text-[10px] px-2 py-0.5 rounded bg-sky-500 text-black font-bold hover:bg-sky-400"
                  data-testid="program-today-edit-workout"
                >
                  Edit
                </button>
              </div>
              {(days[todayKey]?.workout || []).length === 0 ? (
                <div className="text-xs text-white/50 italic py-4 text-center">Nothing planned. Tap Edit or use AI Draft.</div>
              ) : (
                <ul className="space-y-1.5">
                  {days[todayKey].workout.map((w, i) => (
                    <li key={i} className="text-xs text-white/90 border-l-2 border-sky-400/60 pl-2" data-testid={`program-today-workout-${i}`}>
                      <span className="font-bold">{w.name}</span>
                      {w.sets && <span className="text-white/60"> · {w.sets}×{w.reps || "—"}</span>}
                      {w.notes && <div className="text-[10px] text-white/50 italic">{w.notes}</div>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {/* Food column */}
            <div className="rounded-lg border border-white/10 bg-black/40 p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest text-amber-300 font-bold">
                  <ChefHat size={11} className="inline" /> Today · food
                </div>
                <button
                  type="button"
                  onClick={() => { setEditorTab("food"); openEditor(todayKey); }}
                  className="text-[10px] px-2 py-0.5 rounded bg-amber-500 text-black font-bold hover:bg-amber-400"
                  data-testid="program-today-edit-food"
                >
                  Edit
                </button>
              </div>
              {(days[todayKey]?.meals || []).length === 0 ? (
                <div className="text-xs text-white/50 italic py-4 text-center">No meals planned. Tap Edit or AI Draft.</div>
              ) : (
                <ul className="space-y-1.5">
                  {days[todayKey].meals.map((m, i) => (
                    <li key={i} className="text-xs text-white/90 border-l-2 border-amber-400/60 pl-2" data-testid={`program-today-meal-${i}`}>
                      <span className="text-[9px] uppercase tracking-widest text-amber-200/80 mr-1">{m.meal}</span>
                      <span className="font-bold">{m.name}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}

      {/* View: week (7 days) */}
      {view === "week" && (
        <div data-testid="program-week">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold mb-1.5">
            Week of {fmtShort(weekDays[0])}
          </div>
          <div className="grid grid-cols-7 gap-1.5">
            {weekDays.map((d) => renderDayCell(d, "md"))}
          </div>
        </div>
      )}

      {/* View: cycle (all weeks) */}
      {view === "cycle" && (
        <div className="space-y-2" data-testid="program-cycle">
          {Array.from({ length: weeks }, (_, w) => (
            <div key={w}>
              <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">
                Week {w + 1} · <span style={{ color: (phaseById[program.phases?.[w]] || {}).color }}>
                  {(phaseById[program.phases?.[w]] || {}).label || "—"}
                </span>
              </div>
              <div className="grid grid-cols-7 gap-1">
                {dayList.slice(w * 7, w * 7 + 7).map((d) => renderDayCell(d, "sm"))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ─── Day editor ─── */}
      {editorOpen && (
        <div className="fixed inset-0 z-[70] bg-black/70 flex items-center justify-center p-3" data-testid="program-day-editor">
          <div ref={editorScrollRef} className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-xl border border-white/20 bg-neutral-950 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-sky-300 font-bold">
                  {fmtShort(selectedDay)}
                </div>
                <div className="text-sm font-bold text-white">Edit day</div>
              </div>
              <button
                type="button"
                onClick={() => setEditorOpen(false)}
                className="p-1.5 rounded bg-black/60 border border-white/20 hover:bg-white/10"
                aria-label="Close"
                data-testid="program-editor-close"
              >
                <X size={14} className="text-white" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1">
              {["workout", "food"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setEditorTab(t)}
                  className={`flex-1 px-2 py-1.5 rounded text-xs font-bold uppercase tracking-widest border transition ${
                    editorTab === t
                      ? (t === "workout" ? "bg-sky-500 border-sky-300 text-black" : "bg-amber-500 border-amber-300 text-black")
                      : "bg-black/40 border-white/15 text-white/70 hover:border-white/30"
                  }`}
                  data-testid={`program-editor-tab-${t}`}
                >
                  {t === "workout" ? <><Dumbbell size={10} className="inline mr-1" /> Workout</> : <><ChefHat size={10} className="inline mr-1" /> Food</>}
                </button>
              ))}
            </div>

            {editorTab === "workout" ? (
              <div className="space-y-2" data-testid="program-editor-workout">
                <ul className="space-y-1.5">
                  {dayWorkout.map((w, i) => (
                    <li key={i} className="flex items-start gap-2 p-2 rounded bg-black/50 border border-white/10">
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-bold text-white truncate">{w.name}</div>
                        {(w.sets || w.reps) && <div className="text-[10px] text-white/60">{w.sets || "—"} × {w.reps || "—"}</div>}
                        {w.notes && <div className="text-[10px] text-white/50 italic">{w.notes}</div>}
                      </div>
                      <button
                        type="button"
                        onClick={() => setDayWorkout((prev) => prev.filter((_, ix) => ix !== i))}
                        className="p-1 rounded bg-black/40 hover:bg-red-500/40"
                        aria-label="Remove"
                        data-testid={`program-editor-workout-remove-${i}`}
                      >
                        <X size={12} className="text-red-200" />
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="rounded border border-white/15 bg-black/40 p-2 space-y-1.5">
                  <input
                    type="text"
                    value={newWName}
                    onChange={(e) => setNewWName(e.target.value)}
                    placeholder="Exercise name (e.g. Front squat)"
                    className="w-full bg-black/60 border border-white/15 rounded px-2 py-1 text-sm text-white placeholder-white/40 outline-none focus:border-sky-400/60"
                    data-testid="program-editor-workout-name"
                  />
                  <div className="flex gap-1.5">
                    <input
                      type="number"
                      value={newWSets}
                      onChange={(e) => setNewWSets(e.target.value)}
                      placeholder="sets"
                      className="flex-1 bg-black/60 border border-white/15 rounded px-2 py-1 text-sm text-white placeholder-white/40 outline-none focus:border-sky-400/60"
                      data-testid="program-editor-workout-sets"
                    />
                    <input
                      type="text"
                      value={newWReps}
                      onChange={(e) => setNewWReps(e.target.value)}
                      placeholder="reps (e.g. 8-10)"
                      className="flex-1 bg-black/60 border border-white/15 rounded px-2 py-1 text-sm text-white placeholder-white/40 outline-none focus:border-sky-400/60"
                      data-testid="program-editor-workout-reps"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!newWName.trim()) return;
                        setDayWorkout((prev) => [...prev, { name: newWName.trim(), sets: newWSets ? Number(newWSets) : undefined, reps: newWReps.trim() || undefined }]);
                        setNewWName(""); setNewWSets(""); setNewWReps("");
                      }}
                      className="px-3 rounded bg-sky-500 text-black font-bold hover:bg-sky-400"
                      data-testid="program-editor-workout-add"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-2" data-testid="program-editor-food">
                <ul className="space-y-1.5">
                  {dayMeals.map((m, i) => (
                    <li key={i} className="flex items-start gap-2 p-2 rounded bg-black/50 border border-white/10">
                      <div className="flex-1 min-w-0">
                        <div className="text-[9px] uppercase tracking-widest text-amber-300">{m.meal}</div>
                        <div className="text-sm font-bold text-white truncate">{m.name}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setDayMeals((prev) => prev.filter((_, ix) => ix !== i))}
                        className="p-1 rounded bg-black/40 hover:bg-red-500/40"
                        aria-label="Remove"
                        data-testid={`program-editor-meal-remove-${i}`}
                      >
                        <X size={12} className="text-red-200" />
                      </button>
                    </li>
                  ))}
                </ul>
                <div className="rounded border border-white/15 bg-black/40 p-2 space-y-1.5">
                  <div className="flex gap-1.5">
                    <select
                      value={newMMeal}
                      onChange={(e) => setNewMMeal(e.target.value)}
                      className="bg-black/60 border border-white/15 rounded px-2 py-1 text-sm text-white outline-none focus:border-amber-400/60"
                      data-testid="program-editor-meal-slot"
                    >
                      {["Breakfast", "Lunch", "Dinner", "Snack"].map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <input
                      type="text"
                      value={newMName}
                      onChange={(e) => setNewMName(e.target.value)}
                      placeholder="Meal name (e.g. Greek yogurt bowl)"
                      className="flex-1 bg-black/60 border border-white/15 rounded px-2 py-1 text-sm text-white placeholder-white/40 outline-none focus:border-amber-400/60"
                      data-testid="program-editor-meal-name"
                    />
                    <button
                      type="button"
                      onClick={() => {
                        if (!newMName.trim()) return;
                        setDayMeals((prev) => [...prev, { meal: newMMeal, name: newMName.trim() }]);
                        setNewMName("");
                      }}
                      className="px-3 rounded bg-amber-500 text-black font-bold hover:bg-amber-400"
                      data-testid="program-editor-meal-add"
                    >
                      <Plus size={12} />
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={onClearDay}
                className="px-3 py-2 rounded bg-black/60 border border-white/20 text-white text-xs font-bold hover:bg-white/10"
                data-testid="program-editor-clear"
              >
                <Trash2 size={12} className="inline mr-1" /> Clear day
              </button>
              <button
                type="button"
                onClick={onSaveDay}
                disabled={busy}
                className="flex-1 px-3 py-2 rounded bg-emerald-500 text-black text-xs font-black uppercase tracking-widest hover:bg-emerald-400 disabled:opacity-60"
                data-testid="program-editor-save"
              >
                {busy ? <Loader2 size={12} className="animate-spin inline" /> : <Check size={12} className="inline mr-1" />}
                Save day
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ProgramPlanner;
